import { useEffect, useMemo } from "react"

import { siliconColor } from "../Avatar/avatarColor"
import { reachOut } from "../../hooks/useLink"
import { useIsMobile } from "../../hooks/useDevice"
import { useInspectStore } from "../../stores/useInspectStore"
import { useLinkStore } from "../../stores/useLinkStore"
import { useNetworkStore } from "../../stores/useNetworkStore"
import { usePlayerStore } from "../../stores/usePlayerStore"
import {
    derivePillarScores,
    PILLAR_LABELS,
    PILLAR_SUBTITLES,
} from "../../lib/pillarsDerive"

// Domo Cero — Panel 2D de inspección de un Tripulante.
//
// Reemplaza al TelemetryHologram 3D (2026-05-05). Aparece como overlay
// HTML cuando se setea `useInspectStore.inspectId`. Muestra:
//   · Nombre + chip de estado
//   · Índice de Silicio en grande (color por nivel)
//   · 6 pilares con barra horizontal y porcentaje
//   · Botón "Solicitar enlace" (solo si el peer es remoto e inspectId
//     no soy yo)
//   · Botón cerrar
//
// Mobile: panel rolla desde abajo, ancho del viewport menos padding,
// scroll interno si hace falta. Desktop: card centrada con backdrop.
//
// La fuente de los 6 pilares hoy es derivePillarScores() — determinista
// por (clerk_user_id, siliconIndex). Cuando entre en producción el
// bridge real con `scan_vibracional`, los valores reales pisan a estos.

const PILLAR_ORDER: (keyof ReturnType<typeof derivePillarScores>)[] = [
    "fisico",
    "mental",
    "emocional",
    "financiero",
    "vector",
    "orbita",
]

export function PeerInfoPanel() {
    const isMobile = useIsMobile()
    const inspectId = useInspectStore((s) => s.inspectId)
    const close = useInspectStore((s) => s.close)

    const peers = useNetworkStore((s) => s.peers)
    const myId = useNetworkStore((s) => s.selfId)

    const myDisplay = usePlayerStore((s) => s.displayName)
    const mySilicon = usePlayerStore((s) => s.siliconIndex)
    const myAnchored = usePlayerStore((s) => s.isAnchored)

    const linkPhase = useLinkStore((s) => s.phase)
    const linkPeerId = useLinkStore((s) => s.peerId)

    // 2026-05-06 — Cuando el panel se abre automáticamente al sellar
    // un enlace (App.tsx hace `inspect.open(linkPeerId)`) NO queremos
    // oscurecer todo el viewport con el backdrop del modal: el
    // Tripulante recién entró en enlace y necesita seguir viendo el
    // campo. En ese caso el panel se renderea como tarjeta flotante
    // sin backdrop ni blur, click-fuera deshabilitado. En cualquier
    // otro caso (tap manual a un avatar, chequeo de propio Avatar) el
    // backdrop oscuro queda como antes para enfocar la lectura.
    const isAutoLinkedView =
        linkPhase === "sealed" &&
        linkPeerId != null &&
        inspectId === linkPeerId

    // Resolver el target. "self" = local, otro = peer remoto.
    const target = useMemo(() => {
        if (!inspectId) return null
        if (inspectId === "self") {
            return {
                id: myId ?? "self",
                clerkId: myId ?? "self",
                displayName: myDisplay,
                siliconIndex: mySilicon,
                isAnchored: myAnchored,
                isSelf: true,
            }
        }
        const peer = peers.get(inspectId)
        if (!peer) return null
        return {
            id: peer.id,
            clerkId: peer.id,
            displayName: peer.displayName,
            siliconIndex: peer.siliconIndex,
            isAnchored: peer.isAnchored,
            isSelf: false,
        }
    }, [inspectId, peers, myId, myDisplay, mySilicon, myAnchored])

    const pillars = useMemo(() => {
        if (!target) return null
        return derivePillarScores(target.clerkId, target.siliconIndex)
    }, [target?.clerkId, target?.siliconIndex])

    // ESC para cerrar (desktop).
    useEffect(() => {
        if (!inspectId) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") close()
        }
        window.addEventListener("keydown", onKey)
        return () => window.removeEventListener("keydown", onKey)
    }, [inspectId, close])

    if (!inspectId || !target || !pillars) return null

    const accent = siliconColor(target.siliconIndex)

    const showLinkButton =
        !target.isSelf &&
        linkPhase === "idle" &&
        !target.isAnchored

    const onRequestLink = () => {
        if (!showLinkButton) return
        reachOut(target.id)
        // Cerramos el panel para que el Tripulante vea la animación
        // del haz cyan saliendo de su avatar.
        close()
    }

    // ── Mobile: bottom sheet ─────────────────────────────────────────
    if (isMobile) {
        return (
            <div
                className="fixed inset-0 z-40"
                style={{
                    background: isAutoLinkedView
                        ? "transparent"
                        : "rgba(0,0,0,0.55)",
                    backdropFilter: isAutoLinkedView
                        ? "none"
                        : "blur(8px)",
                    WebkitBackdropFilter: isAutoLinkedView
                        ? "none"
                        : "blur(8px)",
                    animation: "peerInfoFadeIn 200ms ease both",
                    pointerEvents: isAutoLinkedView ? "none" : "auto",
                }}
                onClick={isAutoLinkedView ? undefined : close}
            >
                <style>{`
                    @keyframes peerInfoFadeIn {
                        0%   { opacity: 0; }
                        100% { opacity: 1; }
                    }
                    @keyframes peerInfoSlideUp {
                        0%   { opacity: 0; transform: translateY(24px); }
                        100% { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: "absolute",
                        left: "calc(env(safe-area-inset-left, 0px) + 10px)",
                        right: "calc(env(safe-area-inset-right, 0px) + 10px)",
                        bottom:
                            "calc(env(safe-area-inset-bottom, 0px) + 14px)",
                        maxHeight: "82vh",
                        overflowY: "auto",
                        borderRadius: 22,
                        background:
                            "linear-gradient(180deg, rgba(8,14,28,0.92), rgba(2,6,14,0.92))",
                        border: `1px solid ${accent}44`,
                        boxShadow:
                            `0 0 32px ${accent}33, 0 12px 36px rgba(0,0,0,0.6)`,
                        padding: 20,
                        color: "#E8F4FF",
                        fontFamily: "Inter, system-ui, sans-serif",
                        animation: "peerInfoSlideUp 240ms cubic-bezier(0.2, 0.8, 0.2, 1) both",
                        pointerEvents: "auto",
                    }}
                >
                    <PanelHeader
                        name={target.displayName}
                        accent={accent}
                        isSelf={target.isSelf}
                        isAnchored={target.isAnchored}
                        onClose={close}
                    />
                    <SiliconIndexBlock idx={target.siliconIndex} accent={accent} />
                    <PillarsList pillars={pillars} accent={accent} />
                    <PanelFooter
                        showLinkButton={showLinkButton}
                        accent={accent}
                        onRequestLink={onRequestLink}
                        isSelf={target.isSelf}
                        isAnchored={target.isAnchored}
                        linkPhase={linkPhase}
                    />
                </div>
            </div>
        )
    }

    // ── Desktop: card centrada (auto-abierto al sellar) o esquina
    //    superior derecha (modo discreto sin backdrop) ─────────────
    return (
        <div
            className="fixed inset-0 z-40"
            style={{
                background: isAutoLinkedView
                    ? "transparent"
                    : "rgba(0,0,0,0.4)",
                backdropFilter: isAutoLinkedView ? "none" : "blur(8px)",
                WebkitBackdropFilter: isAutoLinkedView
                    ? "none"
                    : "blur(8px)",
                display: "flex",
                alignItems: isAutoLinkedView ? "flex-start" : "center",
                justifyContent: isAutoLinkedView
                    ? "flex-end"
                    : "center",
                paddingTop: isAutoLinkedView ? 80 : 0,
                paddingRight: isAutoLinkedView ? 24 : 0,
                animation: "peerInfoFadeIn 200ms ease both",
                pointerEvents: isAutoLinkedView ? "none" : "auto",
            }}
            onClick={isAutoLinkedView ? undefined : close}
        >
            <style>{`
                @keyframes peerInfoFadeIn {
                    0%   { opacity: 0; }
                    100% { opacity: 1; }
                }
                @keyframes peerInfoScaleIn {
                    0%   { opacity: 0; transform: scale(0.96) translateY(8px); }
                    100% { opacity: 1; transform: scale(1) translateY(0); }
                }
            `}</style>
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: isAutoLinkedView ? 360 : 420,
                    maxWidth: "92vw",
                    maxHeight: "86vh",
                    overflowY: "auto",
                    borderRadius: 18,
                    background:
                        "linear-gradient(180deg, rgba(8,14,28,0.93), rgba(2,6,14,0.93))",
                    border: `1px solid ${accent}44`,
                    boxShadow:
                        `0 0 36px ${accent}3a, 0 20px 60px rgba(0,0,0,0.65)`,
                    padding: isAutoLinkedView ? 18 : 24,
                    color: "#E8F4FF",
                    fontFamily: "Inter, system-ui, sans-serif",
                    animation: "peerInfoScaleIn 220ms cubic-bezier(0.2, 0.8, 0.2, 1) both",
                    pointerEvents: "auto",
                }}
            >
                <PanelHeader
                    name={target.displayName}
                    accent={accent}
                    isSelf={target.isSelf}
                    isAnchored={target.isAnchored}
                    onClose={close}
                />
                <SiliconIndexBlock idx={target.siliconIndex} accent={accent} />
                <PillarsList pillars={pillars} accent={accent} />
                <PanelFooter
                    showLinkButton={showLinkButton}
                    accent={accent}
                    onRequestLink={onRequestLink}
                    isSelf={target.isSelf}
                    isAnchored={target.isAnchored}
                    linkPhase={linkPhase}
                />
            </div>
        </div>
    )
}

// ──────────────────────────────────────────────────────────────────────
function PanelHeader({
    name,
    accent,
    isSelf,
    isAnchored,
    onClose,
}: {
    name: string
    accent: string
    isSelf: boolean
    isAnchored: boolean
    onClose: () => void
}) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 16,
            }}
        >
            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        fontSize: 10,
                        letterSpacing: "0.3em",
                        textTransform: "uppercase",
                        color: "rgba(180,220,255,0.7)",
                        marginBottom: 4,
                    }}
                >
                    Telemetría {isSelf ? "· Tu Avatar" : "· Tripulante"}
                </div>
                <div
                    style={{
                        fontSize: 18,
                        fontWeight: 500,
                        color: "#fff",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {name}
                </div>
                {isAnchored && (
                    <div
                        style={{
                            display: "inline-block",
                            marginTop: 4,
                            padding: "3px 10px",
                            borderRadius: 999,
                            border: "1px solid rgba(212,175,55,0.5)",
                            color: "#D4AF37",
                            fontSize: 9.5,
                            letterSpacing: "0.24em",
                            textTransform: "uppercase",
                        }}
                    >
                        ✦ Anclado
                    </div>
                )}
            </div>
            <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    border: `1px solid ${accent}66`,
                    background: "rgba(8,16,32,0.7)",
                    color: "#E8F4FF",
                    fontSize: 16,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                ×
            </button>
        </div>
    )
}

function SiliconIndexBlock({
    idx,
    accent,
}: {
    idx: number
    accent: string
}) {
    return (
        <div
            style={{
                padding: "16px 18px",
                borderRadius: 12,
                background:
                    `linear-gradient(135deg, ${accent}1a, rgba(8,14,28,0.5))`,
                border: `1px solid ${accent}33`,
                marginBottom: 18,
                display: "flex",
                alignItems: "baseline",
                gap: 12,
            }}
        >
            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        fontSize: 9.5,
                        letterSpacing: "0.32em",
                        textTransform: "uppercase",
                        color: "rgba(180,220,255,0.7)",
                        marginBottom: 4,
                    }}
                >
                    Índice de Silicio
                </div>
                <div
                    style={{
                        fontSize: 11,
                        color: "rgba(200,220,240,0.65)",
                    }}
                >
                    Promedio del último ciclo del Escáner
                </div>
            </div>
            <div
                style={{
                    fontSize: 44,
                    fontWeight: 200,
                    lineHeight: 1,
                    color: accent,
                    textShadow: `0 0 18px ${accent}88`,
                }}
            >
                {idx}
                <span
                    style={{
                        fontSize: 14,
                        opacity: 0.5,
                        marginLeft: 4,
                    }}
                >
                    /100
                </span>
            </div>
        </div>
    )
}

function PillarsList({
    pillars,
    accent,
}: {
    pillars: ReturnType<typeof derivePillarScores>
    accent: string
}) {
    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                marginBottom: 18,
            }}
        >
            <div
                style={{
                    fontSize: 9.5,
                    letterSpacing: "0.32em",
                    textTransform: "uppercase",
                    color: "rgba(180,220,255,0.7)",
                    marginBottom: 4,
                }}
            >
                Los 6 Pilares
            </div>
            {PILLAR_ORDER.map((k) => (
                <PillarRow
                    key={k}
                    label={PILLAR_LABELS[k]}
                    subtitle={PILLAR_SUBTITLES[k]}
                    value={pillars[k]}
                    accent={accent}
                />
            ))}
        </div>
    )
}

function PillarRow({
    label,
    subtitle,
    value,
    accent,
}: {
    label: string
    subtitle: string
    value: number
    accent: string
}) {
    const color = siliconColor(value)
    return (
        <div>
            <div
                style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    marginBottom: 4,
                }}
            >
                <div
                    style={{
                        fontSize: 12,
                        letterSpacing: "0.06em",
                        color: "#E8F4FF",
                    }}
                >
                    <span style={{ fontWeight: 500 }}>{label}</span>
                    <span
                        style={{
                            color: "rgba(180,200,220,0.55)",
                            marginLeft: 8,
                            fontSize: 10.5,
                            letterSpacing: "0.18em",
                            textTransform: "uppercase",
                        }}
                    >
                        {subtitle}
                    </span>
                </div>
                <div
                    style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color,
                    }}
                >
                    {value}
                </div>
            </div>
            <div
                style={{
                    width: "100%",
                    height: 6,
                    borderRadius: 999,
                    background: "rgba(255,255,255,0.06)",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        width: `${value}%`,
                        height: "100%",
                        background:
                            `linear-gradient(90deg, ${color}cc, ${accent})`,
                        boxShadow: `0 0 10px ${color}88`,
                        transition: "width 280ms cubic-bezier(0.2, 0.8, 0.2, 1)",
                    }}
                />
            </div>
        </div>
    )
}

function PanelFooter({
    showLinkButton,
    accent,
    onRequestLink,
    isSelf,
    isAnchored,
    linkPhase,
}: {
    showLinkButton: boolean
    accent: string
    onRequestLink: () => void
    isSelf: boolean
    isAnchored: boolean
    linkPhase: string
}) {
    if (showLinkButton) {
        return (
            <button
                type="button"
                onClick={onRequestLink}
                style={{
                    width: "100%",
                    padding: "12px 18px",
                    borderRadius: 999,
                    border: `1px solid ${accent}88`,
                    background:
                        `linear-gradient(90deg, ${accent}cc, ${accent}88)`,
                    color: "#04101A",
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: "0.24em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                    boxShadow: `0 0 22px ${accent}66`,
                }}
            >
                ◈ Solicitar enlace
            </button>
        )
    }
    let hint = ""
    if (isSelf) hint = "Estás viendo tu propia firma."
    else if (isAnchored) hint = "Tripulante anclado · intocable."
    else if (linkPhase === "reaching") hint = "Solicitud de enlace en curso."
    else if (linkPhase === "sealing") hint = "Sellando enlace…"
    else if (linkPhase === "sealed") hint = "Ya estás en enlace."
    if (!hint) return null
    return (
        <div
            style={{
                fontSize: 11,
                color: "rgba(200,220,240,0.6)",
                textAlign: "center",
                padding: "8px 12px",
                borderRadius: 8,
                background: "rgba(255,255,255,0.04)",
                letterSpacing: "0.12em",
            }}
        >
            {hint}
        </div>
    )
}
